// Shared presentational pieces for the tenant workflow-CUSTOMIZATION surfaces — used by both
// WorkflowSettingsCard (the SAM chat card) and the tenant Settings "Workflow" tab
// (WorkflowTenantSettingsSection). Extracted so the two surfaces render the same look/feel
// (toggle switches, rename affordance, badges) without copy-pasting the markup twice.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';
import { Switch } from '@/components/UI/Switch';
import { fieldClass } from '@/components/UI/fieldStyles';

// Values a transition's authority can be granted to (tenant-scoped roles only — platform_admin /
// platform_support are cross-tenant and never assignable here).
export const ROLE_OPTIONS = ['tenant_admin', 'project_manager', 'sales_agent', 'office_admin', 'crew_member'];

export const CATEGORY_OPTIONS = ['standard', 'backward', 'skip', 'hold', 'resume', 'reopen', 'cancel', 'reject', 'corrective_loop'];

export const STAGE_PILL = 'bg-accent-soft text-accent-text';
export const PROCESS_PILL = 'bg-ui-hover text-ui-text';
export const CUSTOM_BADGE = 'bg-accent-soft text-accent-text';
export const INACTIVE_BADGE = 'bg-ui-hover text-ui-text2';

export const FIELD_CLS = fieldClass({ size: 'xs' });

export const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const Badge = ({ label, cls }: { label: string; cls: string }) => (
  <span className={cn('inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-micro font-semibold uppercase tracking-eyebrow', cls)}>{label}</span>
);

// The shared Switch, with the click-only API these settings cards already use.
export const Toggle = ({ checked, disabled, title, onClick }: { checked: boolean; disabled?: boolean; title?: string; onClick: () => void }) => (
  <Switch checked={checked} disabled={disabled} title={title} onClick={onClick} aria-label={title ?? 'Toggle'} />
);

// Inline rename affordance: click the label to reveal a text input (Cancel/Save pair). Disabled
// (no click target) when the item isn't `tenant_label_renamable`.
export const RenameLabel = ({
  code, label, renamable, busy, onRename, className,
}: {
  code: string; label: string; renamable: boolean; busy: boolean; className?: string;
  onRename: (code: string, newLabel: string) => void;
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);

  if (editing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={cn(FIELD_CLS, 'min-w-0 flex-1')}
        />
        <Button variant="ghost" size="xs" className="shrink-0" onClick={() => setEditing(false)}>
          {t('workflow.cancel', 'Cancel')}
        </Button>
        <Button
          size="xs" className="shrink-0" loading={busy}
          disabled={!value.trim() || value.trim() === label}
          onClick={() => { onRename(code, value.trim()); setEditing(false); }}
        >
          {t('workflowSettings.save', 'Save')}
        </Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { if (renamable) { setValue(label); setEditing(true); } }}
      disabled={!renamable}
      title={renamable ? t('workflowSettings.renameHint', 'Click to rename') : t('workflowSettings.notRenamable', 'This label cannot be renamed.')}
      className={cn(
        'truncate text-left font-medium text-ui-text',
        renamable ? 'cursor-pointer decoration-dotted hover:underline' : 'cursor-default',
        className,
      )}
    >
      {label}
    </button>
  );
};
